// Minimum spacing for the reconnect panel catch-up (CYBORG-1205 item 4). coalesceBurst
// only stops OVERLAPPING bursts; a reconnect that lands just after one finished ran the
// whole panel read again (~200 KB). A call within `windowMs` of the last run's end is
// deferred to the window's end, and every call in between shares that one run (latest args).

export function spacedCatchUp<A extends unknown[]>(
  run: (...args: A) => Promise<void>,
  windowMs: number,
  now: () => number = Date.now,
  setTimer: (fn: () => void, ms: number) => unknown = setTimeout,
): (...args: A) => Promise<void> {
  let lastEnd = Number.NEGATIVE_INFINITY;
  let inFlight = false;
  let armed = false;
  let latest: A | null = null;

  const arm = (): void => {
    if (armed) return;
    armed = true;
    setTimer(
      () => {
        armed = false;
        const args = latest;
        latest = null;
        if (args) void start(args);
      },
      Math.max(0, lastEnd + windowMs - now()),
    );
  };

  const start = async (args: A): Promise<void> => {
    inFlight = true;
    try {
      await run(...args);
    } finally {
      inFlight = false;
      lastEnd = now();
      if (latest) arm();
    }
  };

  return (...args: A): Promise<void> => {
    if (!inFlight && !armed && now() - lastEnd >= windowMs) return start(args);
    latest = args;
    if (!inFlight) arm();
    return Promise.resolve();
  };
}
